// Registry of tunable numbers. Gameplay code reads values from here so the
// dev tuning panel can change them live while driving.

export interface ParamSpec {
  id: string;
  label: string;
  group: string;
  min: number;
  max: number;
  step: number;
  value: number;
}

export interface Param extends ParamSpec {
  readonly defaultValue: number;
}

const registry = new Map<string, Param>();
const STORAGE_KEY = 'fone.params';

function loadOverrides(): Record<string, number> {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, number>) : {};
  } catch {
    return {};
  }
}

let overrides: Record<string, number> | undefined;

export function param(spec: ParamSpec): Param {
  const existing = registry.get(spec.id);
  if (existing) return existing;
  overrides ??= loadOverrides();
  const stored = overrides[spec.id];
  const p: Param = {
    ...spec,
    defaultValue: spec.value,
    value: typeof stored === 'number' && Number.isFinite(stored) ? stored : spec.value,
  };
  registry.set(spec.id, p);
  return p;
}

export function allParams(): Param[] {
  return [...registry.values()];
}

export function setParam(id: string, value: number): void {
  const p = registry.get(id);
  if (!p) return;
  p.value = value;
  persist();
}

export function resetParams(): void {
  for (const p of registry.values()) p.value = p.defaultValue;
  persist();
}

function persist(): void {
  const changed: Record<string, number> = {};
  for (const p of registry.values()) {
    if (p.value !== p.defaultValue) changed[p.id] = p.value;
  }
  overrides = changed;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(changed));
  } catch {
    // Storage can be unavailable (private mode); tuning still works in memory.
  }
}
